import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getEstimate, submitLead } from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { TrendingUp } from "lucide-react";

const SUBURBS = [
    "Werribee",
    "Point Cook",
    "Truganina",
    "Tarneit",
    "Hoppers Crossing",
    "Wyndham Vale",
    "Melton",
    "Caroline Springs",
    "Geelong",
    "Armstrong Creek",
    "Ocean Grove",
    "Torquay",
    "Altona",
    "Williamstown",
    "Footscray",
    "Sunshine",
    "Melbourne CBD",
    "Southbank",
    "St Kilda",
    "Other",
];

const TYPES = [
    { value: "house", label: "House" },
    { value: "townhouse", label: "Townhouse" },
    { value: "apartment", label: "Apartment" },
    { value: "penthouse", label: "Penthouse" },
    { value: "studio", label: "Studio" },
];

const QUALITY = [
    { value: "standard", label: "Standard" },
    { value: "premium", label: "Premium" },
    { value: "luxury", label: "Luxury" },
];

export default function IncomeCalculator() {
    const [form, setForm] = useState({
        bedrooms: 3,
        suburb: "Werribee",
        property_type: "house",
        quality: "premium",
    });
    const [result, setResult] = useState(null);
    const [lead, setLead] = useState({ name: "", email: "", phone: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;
        getEstimate(form)
            .then((d) => {
                if (active) setResult(d);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [form]);

    const handleSendReport = async () => {
        if (!lead.name || !lead.email) {
            toast.error("Add your name and email to receive the full report.");
            return;
        }
        setBusy(true);
        try {
            await submitLead({
                ...lead,
                source: "estimate",
                suburb: form.suburb,
                bedrooms: form.bedrooms,
                property_type: form.property_type,
                estimated_monthly: result?.monthly_avg ?? null,
                message: `Quality tier: ${form.quality}`,
            });
            toast.success("Report on its way — we'll be in touch shortly.");
            setLead({ name: "", email: "", phone: "" });
        } catch {
            toast.error("Couldn't submit — please call 0469 076 071.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="income-calculator"
            className="bg-night border border-line-dark p-8 md:p-14 grid grid-cols-1 lg:grid-cols-5 gap-12"
        >
            {/* Inputs */}
            <div className="lg:col-span-3">
                <div className="eyebrow mb-3">Income Estimator</div>
                <h3 className="headline text-3xl md:text-5xl mb-3 text-cream">
                    What could your property earn?
                </h3>
                <p className="text-cream/55 font-light max-w-xl mb-10">
                    A live, indicative revenue estimate calibrated against
                    PriceLabs short-stay data for Western Melbourne &amp; the
                    Geelong corridor. Adjust the levers and we&rsquo;ll send you
                    the full report.
                </p>

                <div className="space-y-8">
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <label className="eyebrow text-fog">Bedrooms</label>
                            <span className="font-serif text-2xl text-cream">
                                {form.bedrooms}
                            </span>
                        </div>
                        <Slider
                            data-testid="calc-bedrooms-slider"
                            value={[form.bedrooms]}
                            onValueChange={([v]) =>
                                setForm((f) => ({ ...f, bedrooms: v }))
                            }
                            min={1}
                            max={6}
                            step={1}
                            className="[&_[role=slider]]:rounded-none [&_[role=slider]]:bg-gold [&_[role=slider]]:border-gold"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="eyebrow text-fog block mb-3">
                                Suburb
                            </label>
                            <Select
                                value={form.suburb}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, suburb: v }))
                                }
                            >
                                <SelectTrigger
                                    data-testid="calc-suburb-select"
                                    className="rounded-none border-0 border-b border-cream/30 bg-transparent focus:ring-0 text-cream"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    {SUBURBS.map((s) => (
                                        <SelectItem key={s} value={s} className="rounded-none">
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="eyebrow text-fog block mb-3">
                                Type
                            </label>
                            <Select
                                value={form.property_type}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, property_type: v }))
                                }
                            >
                                <SelectTrigger
                                    data-testid="calc-type-select"
                                    className="rounded-none border-0 border-b border-cream/30 bg-transparent focus:ring-0 text-cream"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    {TYPES.map((t) => (
                                        <SelectItem
                                            key={t.value}
                                            value={t.value}
                                            className="rounded-none"
                                        >
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="eyebrow text-fog block mb-3">
                                Finish
                            </label>
                            <Select
                                value={form.quality}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, quality: v }))
                                }
                            >
                                <SelectTrigger
                                    data-testid="calc-quality-select"
                                    className="rounded-none border-0 border-b border-cream/30 bg-transparent focus:ring-0 text-cream"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    {QUALITY.map((q) => (
                                        <SelectItem
                                            key={q.value}
                                            value={q.value}
                                            className="rounded-none"
                                        >
                                            {q.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="hairline mt-12 mb-10 opacity-50" />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                        className="input-line"
                        placeholder="Name"
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        data-testid="calc-lead-name"
                    />
                    <input
                        className="input-line"
                        placeholder="Email"
                        type="email"
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        data-testid="calc-lead-email"
                    />
                    <input
                        className="input-line"
                        placeholder="Phone (optional)"
                        value={lead.phone}
                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                        data-testid="calc-lead-phone"
                    />
                </div>
                <button
                    onClick={handleSendReport}
                    disabled={busy}
                    data-testid="calc-send-report"
                    className="btn-gold mt-8 disabled:opacity-60"
                >
                    {busy ? "Sending..." : "Send My Full Report"}
                </button>
            </div>

            {/* Result panel */}
            <div className="lg:col-span-2 bg-[#0A0A0A] text-cream p-10 md:p-14 relative overflow-hidden grain">
                <div className="relative">
                    <div className="eyebrow text-gold mb-3 flex items-center gap-2">
                        <TrendingUp size={14} strokeWidth={1.4} />
                        Estimated annual revenue
                    </div>
                    <div
                        data-testid="calc-annual-result"
                        className="font-serif text-5xl md:text-7xl leading-none mb-2"
                    >
                        ${result ? result.annual_avg.toLocaleString() : "—"}
                    </div>
                    <div className="text-cream/60 text-sm font-light">
                        Range&nbsp;
                        <span className="text-cream/90">
                            ${result?.annual_low.toLocaleString() ?? "—"} – $
                            {result?.annual_high.toLocaleString() ?? "—"}
                        </span>
                    </div>

                    <div className="hairline my-10 opacity-50" />

                    <dl className="space-y-5 text-sm font-light">
                        <div className="flex justify-between">
                            <dt className="text-cream/60 eyebrow">Avg. daily rate</dt>
                            <dd
                                className="font-serif text-2xl"
                                data-testid="calc-nightly-result"
                            >
                                ${result?.nightly_avg ?? "—"}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-cream/60 eyebrow">Avg. occupancy</dt>
                            <dd className="font-serif text-2xl">
                                {result ? `${result.occupancy_rate}%` : "—"}
                            </dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-cream/60 eyebrow">Monthly avg</dt>
                            <dd
                                className="font-serif text-2xl text-gold"
                                data-testid="calc-monthly-result"
                            >
                                ${result ? result.monthly_avg.toLocaleString() : "—"}
                            </dd>
                        </div>
                    </dl>

                    <p className="text-xs text-cream/40 mt-10 leading-relaxed">
                        Indicative figures benchmarked against PriceLabs market data
                        for Western Melbourne &amp; Geelong short-stays. Your
                        property&rsquo;s tailored forecast may vary based on styling,
                        amenities and seasonality.
                    </p>
                </div>
            </div>
        </div>
    );
}
